"use client"

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
} from "react"

import { Button, cn, Loader } from "@coval/ui"
import { ArrowDown } from "lucide-react"

export interface SofiaTranscriptHandle {
  scrollToLatest: () => void
}

/** Keeps live output in view until the reader chooses to inspect earlier messages. */
export const SofiaTranscript = forwardRef<
  SofiaTranscriptHandle,
  {
    children: ReactNode
    active?: boolean
    working: boolean
    className?: string
  }
>(function SofiaTranscript(
  { children, active = true, working, className },
  ref,
) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const followingRef = useRef(true)
  const lastScrollTopRef = useRef(0)
  const [awayFromLatest, setAwayFromLatest] = useState(false)

  const setFollowing = useCallback((following: boolean) => {
    followingRef.current = following
    setAwayFromLatest(!following)
  }, [])

  const followLatest = useCallback(() => {
    const viewport = viewportRef.current
    if (
      !viewport ||
      !active ||
      viewport.clientHeight === 0 ||
      !followingRef.current
    ) {
      return
    }
    const bottom = Math.max(0, viewport.scrollHeight - viewport.clientHeight)
    // A resize may clamp the old position; moving above that clamp is a reader scrolling up.
    if (viewport.scrollTop < Math.min(lastScrollTopRef.current, bottom) - 1) {
      setFollowing(false)
      return
    }
    viewport.scrollTo({ top: viewport.scrollHeight, behavior: "instant" })
    lastScrollTopRef.current = viewport.scrollTop
  }, [active, setFollowing])

  const scrollToLatest = useCallback(() => {
    lastScrollTopRef.current = viewportRef.current?.scrollTop ?? 0
    setFollowing(true)
    followLatest()
  }, [followLatest, setFollowing])
  useImperativeHandle(ref, () => ({ scrollToLatest }), [scrollToLatest])

  useEffect(() => {
    followLatest()
  }, [children, followLatest])

  useEffect(() => {
    const viewport = viewportRef.current
    const content = contentRef.current
    if (!viewport || !content || typeof ResizeObserver === "undefined") {
      return
    }
    // Markdown, charts and the composer can change the available geometry after a reply renders.
    const observer = new ResizeObserver(followLatest)
    observer.observe(viewport)
    observer.observe(content)
    return () => observer.disconnect()
  }, [followLatest])

  const handleScroll = () => {
    const viewport = viewportRef.current
    if (!active || !viewport || viewport.clientHeight === 0) {
      return
    }
    if (followingRef.current) {
      followLatest()
      lastScrollTopRef.current = viewport.scrollTop
      return
    }
    // Only a downward scroll back to the bottom resumes following, not a delayed layout event.
    const atBottom =
      viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight <= 1
    if (atBottom && viewport.scrollTop > lastScrollTopRef.current) {
      setFollowing(true)
    }
    lastScrollTopRef.current = viewport.scrollTop
  }

  const handleJumpToLatest = () => {
    scrollToLatest()
    viewportRef.current?.focus({ preventScroll: true })
  }

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={viewportRef}
        role="log"
        aria-label="Sofia messages"
        aria-live="polite"
        tabIndex={-1}
        onScroll={handleScroll}
        onWheel={(event) => {
          const viewport = event.currentTarget
          if (
            active &&
            event.deltaY < 0 &&
            viewport.scrollTop > 0 &&
            viewport.scrollHeight > viewport.clientHeight
          ) {
            setFollowing(false)
          }
        }}
        className={cn("min-h-0 flex-1 overflow-y-auto", className)}
      >
        <div ref={contentRef}>{children}</div>
      </div>
      {active && awayFromLatest && (
        <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
          <Button
            type="button"
            variant="secondary"
            icon
            tooltip="Jump to latest"
            aria-label={
              working
                ? "Jump to latest — response in progress"
                : "Jump to latest"
            }
            className="group/latest pointer-events-auto rounded-full shadow-md"
            onClick={handleJumpToLatest}
          >
            <span
              aria-hidden
              className="relative flex size-4 items-center justify-center"
            >
              {working && (
                <Loader
                  size="sm"
                  className="absolute group-hover/latest:invisible group-focus-visible/latest:invisible"
                />
              )}
              <ArrowDown
                className={cn(
                  "size-4",
                  working &&
                    "invisible group-hover/latest:visible group-focus-visible/latest:visible",
                )}
              />
            </span>
          </Button>
        </div>
      )}
    </div>
  )
})
